import type { AttributionField, AttributionParamRule, AttributionProvider } from './types';

/**
 * Canonical query keys that may appear as top-level fields on payloads.
 * Explicit presence of these keys on the URL wins over mappings and builtins.
 */
export const CANONICAL_FIELD_KEYS: Readonly<Record<AttributionField, readonly string[]>> = {
  publisher_key: ['publisher_key'],
  click_id: ['click_id'],
  campaign_id: ['campaign_id'],
  coupon_code: ['coupon_code'],
  utm_source: ['utm_source'],
  utm_medium: ['utm_medium'],
  utm_campaign: ['utm_campaign'],
  utm_term: ['utm_term'],
  utm_content: ['utm_content'],
  sub_id1: ['sub_id1'],
  sub_id2: ['sub_id2'],
  sub_id3: ['sub_id3'],
  sub_id4: ['sub_id4'],
  sub_id5: ['sub_id5'],
};

/**
 * Documentation-backed built-in provider rules.
 * Precedence among builtins is array order (first non-empty wins per field).
 * Keep first-party `ol_aff` first so OneLence direct links outrank network IDs
 * when both appear on the same landing URL.
 */
export const BUILTIN_ATTRIBUTION_RULES: readonly AttributionParamRule[] = [
  // OneLence first-party direct links (highest builtin publisher precedence)
  { key: 'ol_aff', field: 'publisher_key', provider: 'onelence', captureRaw: true },

  // Awin
  { key: 'aw_affid', field: 'publisher_key', provider: 'awin', captureRaw: true },
  { key: 'awc', field: 'click_id', provider: 'awin', captureRaw: true },

  // Impact — gateway templates emit irpid; direct tracking docs use irmp
  { key: 'irpid', field: 'publisher_key', provider: 'impact', captureRaw: true },
  { key: 'irmp', field: 'publisher_key', provider: 'impact', captureRaw: true },
  { key: 'irclickid', field: 'click_id', provider: 'impact', captureRaw: true },

  // CJ — pid is publisher/website ID but collision-prone (product ids); require companion
  { key: 'pid', field: 'publisher_key', provider: 'cj', captureRaw: true, requiresAnyOf: ['cjevent', 'aid', 'sid'] },
  { key: 'cjevent', field: 'click_id', provider: 'cj', captureRaw: true },

  // ShareASale
  { key: 'sscid', field: 'click_id', provider: 'shareasale', captureRaw: true },

  // Partnerize / Performance Horizon (camref is campaign+publisher token; treat as publisher_key)
  { key: 'camref', field: 'publisher_key', provider: 'partnerize', captureRaw: true },
  { key: 'clickref', field: 'click_id', provider: 'partnerize', captureRaw: true },

  // Rakuten Advertising (LinkShare) — site / mid often land on advertiser URLs
  { key: 'ranSiteID', field: 'publisher_key', provider: 'rakuten', captureRaw: true },
  { key: 'ranMID', field: 'campaign_id', provider: 'rakuten', captureRaw: true },
  { key: 'ranEAID', field: 'click_id', provider: 'rakuten', captureRaw: true },

  // Tradedoubler — `a` is affiliate id but too generic alone; require tduid companion
  { key: 'a', field: 'publisher_key', provider: 'tradedoubler', captureRaw: true, requiresAnyOf: ['tduid', 'epi', 'epi2'] },
  { key: 'tduid', field: 'click_id', provider: 'tradedoubler', captureRaw: true },

  // Webgains
  { key: 'wguid', field: 'click_id', provider: 'webgains', captureRaw: true },
  { key: 'wgidentifier', field: 'publisher_key', provider: 'webgains', captureRaw: true },

  // Admitad
  { key: 'admitad_uid', field: 'click_id', provider: 'admitad', captureRaw: true },
  { key: 'publisher_id', field: 'publisher_key', provider: 'admitad', captureRaw: true, requiresAnyOf: ['admitad_uid'] },

  // SaaS / referral platforms (distinctive keys; generics like ref/aff remain below)
  // Rewardful default is ?via=token; FirstPromoter/Dub/Reditus also use via.
  { key: 'via', field: 'publisher_key', provider: 'rewardful', captureRaw: true },
  // FirstPromoter
  { key: 'fpr', field: 'publisher_key', provider: 'firstpromoter', captureRaw: true },
  { key: 'fp_ref', field: 'publisher_key', provider: 'firstpromoter', captureRaw: true },
  // Refersion
  { key: 'rfsn', field: 'publisher_key', provider: 'refersion', captureRaw: true },
  // PartnerStack (GrowSumo) — key is Base64-encoded partner id on landing URLs
  { key: 'ps_partner_key', field: 'publisher_key', provider: 'partnerstack', captureRaw: true },
  { key: 'ps_xid', field: 'click_id', provider: 'partnerstack', captureRaw: true },
  // Post Affiliate Pro
  { key: 'a_aid', field: 'publisher_key', provider: 'postaffiliatepro', captureRaw: true },
  // Talkable
  { key: 'mbsy', field: 'publisher_key', provider: 'talkable', captureRaw: true },
  // iDevAffiliate
  { key: 'idev', field: 'publisher_key', provider: 'idevaffiliate', captureRaw: true },

  // Backward-compatible generic affiliate aliases (inbound only; OL generator emits ol_aff)
  // Note: Rewardful also uses ?referral=<uuid> to preload a referral session id (not a
  // publisher token). Primary Rewardful publisher param is `via`; treat bare `referral`
  // as a publisher code for generic programs. Override via attribution_param_map if needed.
  { key: 'ref', field: 'publisher_key', provider: 'direct', captureRaw: true },
  { key: 'aff', field: 'publisher_key', provider: 'direct', captureRaw: true },
  { key: 'per', field: 'publisher_key', provider: 'direct', captureRaw: true },
  { key: 'affiliate_id', field: 'publisher_key', provider: 'direct', captureRaw: true },
  { key: 'referral', field: 'publisher_key', provider: 'direct', captureRaw: true },

  // Coupons
  { key: 'coupon', field: 'coupon_code', captureRaw: true },
  { key: 'coupon_code', field: 'coupon_code', captureRaw: true },

  // Campaign aliases
  { key: 'cid', field: 'campaign_id', captureRaw: true },
  { key: 'campaign_id', field: 'campaign_id', captureRaw: true },

  // Paid click IDs
  { key: 'ch_click_id', field: 'click_id', captureRaw: true },
  { key: 'gclid', field: 'click_id', provider: 'google', captureRaw: true },
  { key: 'gbraid', field: 'click_id', provider: 'google', captureRaw: true },
  { key: 'wbraid', field: 'click_id', provider: 'google', captureRaw: true },
  { key: 'dclid', field: 'click_id', provider: 'google', captureRaw: true },
  { key: 'msclkid', field: 'click_id', provider: 'microsoft', captureRaw: true },
  { key: 'fbclid', field: 'click_id', provider: 'meta', captureRaw: true },
  { key: 'ttclid', field: 'click_id', provider: 'tiktok', captureRaw: true },
  { key: 'twclid', field: 'click_id', provider: 'twitter', captureRaw: true },
  { key: 'li_fat_id', field: 'click_id', provider: 'linkedin', captureRaw: true },
  { key: 'epik', field: 'click_id', provider: 'pinterest', captureRaw: true },
  { key: 'rdt_cid', field: 'click_id', provider: 'reddit', captureRaw: true },
  { key: 'sccid', field: 'click_id', provider: 'snapchat', captureRaw: true },

  // UTMs
  { key: 'utm_source', field: 'utm_source', captureRaw: true },
  { key: 'utm_medium', field: 'utm_medium', captureRaw: true },
  { key: 'utm_campaign', field: 'utm_campaign', captureRaw: true },
  { key: 'utm_term', field: 'utm_term', captureRaw: true },
  { key: 'utm_content', field: 'utm_content', captureRaw: true },

  // Sub IDs (also accept Impact sharedid / subId* as raw allowlist via keys below)
  { key: 'sub_id1', field: 'sub_id1', captureRaw: true },
  { key: 'sub_id2', field: 'sub_id2', captureRaw: true },
  { key: 'sub_id3', field: 'sub_id3', captureRaw: true },
  { key: 'sub_id4', field: 'sub_id4', captureRaw: true },
  { key: 'sub_id5', field: 'sub_id5', captureRaw: true },
  { key: 'subId1', field: 'sub_id1', captureRaw: true },
  { key: 'subId2', field: 'sub_id2', captureRaw: true },
  { key: 'subId3', field: 'sub_id3', captureRaw: true },
  { key: 'sharedid', field: 'sub_id1', captureRaw: true },
];

/**
 * Raw query storage aliases (source key → stored key).
 * Used only for the allowlisted query bag, not for semantic field resolution.
 */
export const QUERY_KEY_ALIASES: Readonly<Record<string, string>> = {
  referral: 'ref',
  affiliate_id: 'ref',
  ol_aff: 'ol_aff',
  aw_affid: 'aw_affid',
  irpid: 'irpid',
  irmp: 'irmp',
  ch_click_id: 'click_id',
  cid: 'campaign_id',
  coupon: 'coupon',
  coupon_code: 'coupon',
  subId1: 'sub_id1',
  subId2: 'sub_id2',
  subId3: 'sub_id3',
  sharedid: 'sub_id1',
};

/**
 * Default allowlist for raw query capture (union of semantic keys + aliases).
 */
export const DEFAULT_CAPTURE_QUERY_KEYS: readonly string[] = Array.from(
  new Set([
    ...BUILTIN_ATTRIBUTION_RULES.map((rule) => rule.key),
    ...Object.keys(QUERY_KEY_ALIASES),
    ...Object.values(QUERY_KEY_ALIASES),
    'click_id',
    'publisher_key',
    'aid',
    'sid',
    'epi',
    'epi2',
  ])
);

export const DEFAULT_QUERY_DENYLIST = [
  'email',
  'phone',
  'token',
  'auth',
  'password',
  'code',
] as const;

export const DEFAULT_MAX_CAPTURED_QUERY_PARAMS = 30;
export const DEFAULT_MAX_QUERY_PARAM_VALUE_LENGTH = 256;

export const AFFILIATE_PROVIDERS: readonly AttributionProvider[] = [
  'onelence',
  'direct',
  'awin',
  'impact',
  'cj',
  'shareasale',
  'partnerize',
  'rakuten',
  'tradedoubler',
  'webgains',
  'admitad',
  'rewardful',
  'firstpromoter',
  'refersion',
  'partnerstack',
  'postaffiliatepro',
  'talkable',
  'idevaffiliate',
  'custom',
];

export function isAttributionField(value: unknown): value is AttributionField {
  return (
    typeof value === 'string' &&
    (value === 'publisher_key' ||
      value === 'click_id' ||
      value === 'campaign_id' ||
      value === 'coupon_code' ||
      value === 'utm_source' ||
      value === 'utm_medium' ||
      value === 'utm_campaign' ||
      value === 'utm_term' ||
      value === 'utm_content' ||
      value === 'sub_id1' ||
      value === 'sub_id2' ||
      value === 'sub_id3' ||
      value === 'sub_id4' ||
      value === 'sub_id5')
  );
}
